import type { PublicUserProfile, User } from "@fitties/shared";
import { lazy, Suspense } from "react";
import type { Match } from "../types";
import { loadMatchCelebrationModule } from "./matchCelebrationPreload";

const MatchCelebration = lazy(() => loadMatchCelebrationModule().then((mod) => ({ default: mod.MatchCelebration })));

type CelebrationUser = Pick<User, "uid" | "display_name" | "photo_url"> | PublicUserProfile;

export type DiscoveryMatchCelebrationState = {
  match: Match;
  matchedUserId: string;
  matchedUser?: PublicUserProfile;
};

type CelebrationPair = { user1: CelebrationUser; user2: CelebrationUser };

export function resolveDiscoveryCelebrationPair(params: {
  matchData: DiscoveryMatchCelebrationState | null;
  showCelebration: boolean;
  matchUsers: CelebrationPair | undefined;
  matchUsersLoading: boolean;
  viewer: User | null | undefined;
}): CelebrationPair | null {
  const { matchData, showCelebration, matchUsers, matchUsersLoading, viewer } = params;
  if (!matchData || !showCelebration) {
    return null;
  }
  if (matchData.matchedUser && viewer) {
    return { user1: viewer, user2: matchData.matchedUser };
  }
  if (matchUsers && !matchUsersLoading) {
    return matchUsers;
  }
  return null;
}

export function DiscoveryPageCelebrationLayer({
  currentUserId,
  showCelebration,
  matchData,
  celebrationPair,
  onClose,
}: {
  currentUserId: string;
  showCelebration: boolean;
  matchData: DiscoveryMatchCelebrationState | null;
  celebrationPair: CelebrationPair | null;
  onClose: () => void;
}) {
  if (!celebrationPair || !matchData) {
    return null;
  }

  return (
    <Suspense
      fallback={
        <div className="fixed inset-0 z-50 bg-black/80" data-testid="match-celebration-overlay" role="presentation" />
      }
    >
      <DiscoveryMatchCelebrationOverlay
        currentUserId={currentUserId}
        showCelebration={showCelebration}
        matchData={matchData}
        celebrationPair={celebrationPair}
        onClose={onClose}
      />
    </Suspense>
  );
}

export function DiscoveryMatchCelebrationOverlay({
  currentUserId,
  showCelebration,
  matchData,
  celebrationPair,
  onClose,
}: {
  currentUserId: string;
  showCelebration: boolean;
  matchData: DiscoveryMatchCelebrationState | null;
  celebrationPair: CelebrationPair | null;
  onClose: () => void;
}) {
  if (!celebrationPair || !matchData) {
    return null;
  }

  const currentUser = celebrationPair.user1.uid === currentUserId ? celebrationPair.user1 : celebrationPair.user2;
  const matchedUser = celebrationPair.user1.uid === currentUserId ? celebrationPair.user2 : celebrationPair.user1;

  return (
    <MatchCelebration
      currentUser={currentUser}
      matchedUser={matchedUser}
      isVisible={showCelebration}
      onClose={onClose}
      currentUserId={currentUserId}
      {...(matchData.match.chatId ? { chatId: matchData.match.chatId } : {})}
    />
  );
}
